import React from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../atoms'
import { CategoryScore, AMENITY_CATEGORIES } from '@popety-io/types'

import {
  AmenityCategoryCard,
  AmenityCategoryHeader,
  AmenityCategoryInfo,
  AmenityCategoryIcon,
  AmenityCategoryDetails,
  AmenityCategoryTitle,
  AmenityCategorySummary,
  AmenitySubcategoryScroller,
  AmenitySubcategoryPill,
  AmenitySubcategoryPillCount,
  AmenityCategoryScore,
  AmenityScoreValue,
  AmenityScoreMax,
  AmenityList,
  AmenityItem,
  AmenityItemInfo,
  AmenityItemName,
  AmenityItemType,
  AmenityItemDistance,
  AmenityShowMore,
  AmenityExpandArrow,
  AmenityGroupItem,
  AmenityGroupDistance,
  AmenityLineBadges,
  AmenityLineBadge,
} from './Amenities.styles'
import { normalizeAmenityPoiTypeKey } from './amenityTranslation'
import { groupUnnamedAmenities, isUnnamed } from './groupUnnamedAmenities'
import {
  orderedModes,
  bestHeadwayMin,
  formatHeadwayMinutes,
  railLineCount,
  transitLines,
} from './transitDisplay'

/*
  Five badges fit the row beside the modes and the frequency without pushing the
  frequency out; the rest collapse into one "+N" whose tooltip carries them.
*/
const MAX_VISIBLE_LINES = 5

export interface AmenityCardProps {
  categoryScore: CategoryScore
  className?: string
  onPOIClick?: (poi: any) => void
}

/**
 * AmenityCard component displays a single amenity category with its POIs.
 * Features include:
 * - Expandable/collapsible list of amenities
 * - Subcategory filtering with checkboxes
 * - "Show more" functionality for long lists
 * - Distance display for each POI
 *
 * @param categoryScore - Category score data including POIs and subcategories
 * @param className - Optional CSS class name
 */
export const AmenityCard: React.FC<AmenityCardProps> = ({
  categoryScore,
  className,
  onPOIClick,
}) => {
  const { t } = useTranslation('amenities')

  // Local state for expand/collapse - much simpler!
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [maxVisibleItems, setMaxVisibleItems] = React.useState(10)
  const config = AMENITY_CATEGORIES[categoryScore.category]

  /*
    One subtype at a time, `null` meaning all. The previous model started with
    every subtype selected, so clicking "Railway Station" removed it instead of
    isolating it — the opposite of what a filter chip implies.
  */
  const [activeFilter, setActiveFilter] = React.useState<string | null>(null)

  const filteredItems = React.useMemo(() => {
    if (!categoryScore.items || categoryScore.items.length === 0) {
      return []
    }
    if (activeFilter === null) {
      return categoryScore.items
    }
    return categoryScore.items.filter(
      (item) => item.sub_category === activeFilter
    )
  }, [categoryScore.items, activeFilter])

  /*
    Group before slicing. Around half the Wellness and Basic Amenities POIs near
    a Swiss address are unnamed facilities, so grouping after the cut would let a
    single type fill the visible window with identical rows.
  */
  const unnamedBySubCategory = React.useMemo(() => {
    const map = new Map<string, number>()
    for (const sub of categoryScore.sub_categories ?? []) {
      if (typeof sub.unnamed_count === 'number') {
        map.set(sub.name, sub.unnamed_count)
      }
    }
    return map
  }, [categoryScore.sub_categories])

  const rows = React.useMemo(
    () => groupUnnamedAmenities(filteredItems, unnamedBySubCategory),
    [filteredItems, unnamedBySubCategory]
  )

  const visibleRows = isExpanded
    ? rows.slice(0, maxVisibleItems)
    : rows.slice(0, 5)
  const hasMore = isExpanded ? rows.length > maxVisibleItems : rows.length > 5
  const remainingCount = isExpanded
    ? Math.max(0, rows.length - maxVisibleItems)
    : Math.max(0, rows.length - 5)

  const handleToggle = () => {
    setIsExpanded(!isExpanded)

    if (isExpanded) {
      setMaxVisibleItems(10)
      setActiveFilter(null)
    }
  }

  /** `null` selects every subtype. Clicking the active chip clears back to all. */
  const handleSubcategoryClick = (
    e: React.MouseEvent,
    subCategory: string | null
  ) => {
    e.stopPropagation() // Don't collapse the card

    setActiveFilter((prev) => (prev === subCategory ? null : subCategory))
    setMaxVisibleItems(10)

    if (!isExpanded) {
      setIsExpanded(true)
    }
  }

  const formatDistance = (distance: number): string => {
    if (distance < 1000) {
      return `${Math.round(distance)}m`
    }
    return `${(distance / 1000).toFixed(1)}km`
  }

  /** Items arrive distance-sorted, so the first one is the nearest. */
  const nearestDistance = categoryScore.items?.[0]?.distance ?? null

  const showMoreItems = () => {
    const newMax = maxVisibleItems + 10
    // If we'll show all or almost all, just show everything
    if (filteredItems.length - newMax <= 10) {
      setMaxVisibleItems(filteredItems.length)
    } else {
      setMaxVisibleItems(newMax)
    }
  }

  return (
    <AmenityCategoryCard
      expanded={isExpanded}
      className={className}
      data-category={categoryScore.category}
    >
      <AmenityCategoryHeader onClick={handleToggle}>
        <AmenityCategoryInfo>
          <AmenityCategoryIcon color={config.color}>
            <Icon name={config.icon as any} size="large" />
          </AmenityCategoryIcon>
          <AmenityCategoryDetails>
            <AmenityCategoryTitle>
              {t(`categories.${categoryScore.category}`, config.label)}
            </AmenityCategoryTitle>
            <AmenityCategorySummary>
              {t('categoryCard.placesCount', {
                count: categoryScore.count,
                defaultValue: `${categoryScore.count} places`,
              })}
              {nearestDistance !== null && (
                <>
                  {' · '}
                  {t('categoryCard.fromDistance', {
                    distance: formatDistance(nearestDistance),
                    defaultValue: `from ${formatDistance(nearestDistance)}`,
                  })}
                </>
              )}
            </AmenityCategorySummary>
          </AmenityCategoryDetails>
        </AmenityCategoryInfo>

        <AmenityCategoryScore>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <AmenityScoreValue variant={categoryScore.score.category}>
              {categoryScore.score.value}
            </AmenityScoreValue>
            <AmenityScoreMax>/100</AmenityScoreMax>
          </div>
          <AmenityExpandArrow $expanded={isExpanded}>
            <Icon name="arrow-down" size="medium" />
          </AmenityExpandArrow>
        </AmenityCategoryScore>
      </AmenityCategoryHeader>

      {/*
        A sibling of the header, not a child of its text column: nested inside,
        the row was inset by the icon on the left and squeezed by the score on
        the right. Full width lets it use the card's own padding and gives the
        scroll its full run.
      */}
      <AmenitySubcategoryScroller onClick={(e) => e.stopPropagation()}>
        {/* `All` leads and is the default, so the chips read as a filter. */}
        <AmenitySubcategoryPill
          type="button"
          $active={activeFilter === null}
          $color={config.color}
          aria-pressed={activeFilter === null}
          onClick={(e) => handleSubcategoryClick(e, null)}
        >
          {t('categoryCard.allSubcategories', { defaultValue: 'All' })}
          <AmenitySubcategoryPillCount>
            {categoryScore.count}
          </AmenitySubcategoryPillCount>
        </AmenitySubcategoryPill>
        {categoryScore.sub_categories?.map((subCat) => (
          <AmenitySubcategoryPill
            key={subCat.id}
            type="button"
            $active={activeFilter === subCat.name}
            $color={config.color}
            aria-pressed={activeFilter === subCat.name}
            onClick={(e) => handleSubcategoryClick(e, subCat.name)}
          >
            {t(`subcategories.${subCat.name}`, subCat.name)}
            {typeof subCat.count === 'number' && (
              <AmenitySubcategoryPillCount>
                {subCat.count}
              </AmenitySubcategoryPillCount>
            )}
          </AmenitySubcategoryPill>
        ))}
      </AmenitySubcategoryScroller>

      <AmenityList $expanded={isExpanded}>
        {/*
          The "showing X of Y / clear filters" banner is gone: the `All` chip
          both states the total and clears the filter, so the banner was a
          second control for the same job.
        */}

        {visibleRows.length === 0 ? (
          <div
            style={{
              padding: '16px 0',
              textAlign: 'center',
              color: '#6b6b6b',
              fontSize: '14px',
              fontStyle: 'italic',
            }}
          >
            {!categoryScore.items || categoryScore.items.length === 0
              ? t('categoryCard.noAmenities')
              : filteredItems.length === 0
                ? t('categoryCard.noMatchingFilters')
                : t('noData')}
          </div>
        ) : (
          <>
            {visibleRows.map((row) => {
              const typeLabel = (type: string) =>
                t(`poiTypes.${normalizeAmenityPoiTypeKey(type)}`, type)

              /*
                A stop's `type` is only its highest mode, so "Tram Stop" hid the
                bus that also calls there — around a city parcel half the stops
                serve more than one. Show every mode, plus the frequency, which
                is what the pipeline's transport score actually keys on.
              */
              const transitLabel = (poi: any): React.ReactNode | null => {
                const modes = orderedModes(poi?.transit)
                if (!modes.length) return null

                /*
                  Segments, each dropped when its datum is missing rather than
                  rendered empty: only train stations carry a line count (6% of
                  stops), and a fifth of stops have no headway at all.
                */
                const segments: React.ReactNode[] = [
                  modes
                    .map((mode) => t(`transitModes.${mode}`, mode))
                    .join(' · '),
                ]

                /*
                  Named lines when they exist, the bare count when they do not.
                  `transit.lines` arrives with a later ingestion run, so every
                  document is on the second rung today and the row has to look
                  finished either way. Never both: the count is distinct GTFS
                  route ids and the list is distinct short names, so one line
                  under two route ids would print "48" beside 47 badges.
                */
                const lines = transitLines(poi?.transit)
                if (lines.length) {
                  const shown = lines.slice(0, MAX_VISIBLE_LINES)
                  const hidden = lines.slice(MAX_VISIBLE_LINES)
                  segments.push(
                    <AmenityLineBadges key="lines">
                      {shown.map((line) => (
                        <AmenityLineBadge key={line.key}>
                          {line.label}
                        </AmenityLineBadge>
                      ))}
                      {hidden.length > 0 && (
                        <AmenityLineBadge
                          $muted
                          title={hidden.map((line) => line.label).join(', ')}
                        >
                          +{hidden.length}
                        </AmenityLineBadge>
                      )}
                    </AmenityLineBadges>
                  )
                } else {
                  const count = railLineCount(poi?.transit)
                  if (count !== null) {
                    segments.push(
                      t('categoryCard.lineCount', {
                        count,
                        defaultValue: count === 1 ? '1 line' : `${count} lines`,
                      })
                    )
                  }
                }

                const headway = bestHeadwayMin(poi?.transit)
                if (headway !== null) {
                  const minutes = formatHeadwayMinutes(headway)
                  segments.push(
                    /*
                      `count` drives the plural: rounding put a 1.2-minute
                      headway at "every 1 min", which reads as broken French
                      ("toutes les 1 min") on exactly the best-served stops.
                    */
                    t('categoryCard.everyMinutes', {
                      count: Number(minutes),
                      minutes,
                      defaultValue:
                        minutes === '1'
                          ? 'every minute'
                          : `every ${minutes} min`,
                    })
                  )
                }

                return segments.map((segment, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && ' · '}
                    {segment}
                  </React.Fragment>
                ))
              }

              /*
                Unnamed facilities — pitches, playgrounds, recycling points —
                collapse into one counted row per type, placed at the distance
                of the nearest member. Same bold-then-grey anatomy as a named
                place, so the list keeps one visual grammar.
              */
              if (row.kind === 'group') {
                return (
                  <AmenityGroupItem
                    key={`group-${row.type}`}
                    $color={config.color}
                  >
                    <AmenityItemInfo>
                      <AmenityItemName>{typeLabel(row.type)}</AmenityItemName>
                      <AmenityItemType>
                        {t('categoryCard.groupNearby', {
                          count: row.count,
                          defaultValue: `${row.count} nearby`,
                        })}
                      </AmenityItemType>
                    </AmenityItemInfo>
                    <AmenityGroupDistance>
                      {t('categoryCard.fromDistance', {
                        distance: formatDistance(row.distance),
                        defaultValue: `from ${formatDistance(row.distance)}`,
                      })}
                    </AmenityGroupDistance>
                  </AmenityGroupItem>
                )
              }

              const item = row.poi
              return (
                <AmenityItem
                  key={item.id}
                  onClick={() => onPOIClick?.(item)}
                  style={{ cursor: onPOIClick ? 'pointer' : 'default' }}
                >
                  <AmenityItemInfo>
                    {/* Never render a fabricated name — the type is the identity. */}
                    <AmenityItemName>
                      {isUnnamed(item) ? typeLabel(item.type) : item.name}
                    </AmenityItemName>
                    {!isUnnamed(item) && (
                      <AmenityItemType>
                        {transitLabel(item) ?? typeLabel(item.type)}
                      </AmenityItemType>
                    )}
                  </AmenityItemInfo>
                  <AmenityItemDistance>
                    {formatDistance(item.distance)}
                  </AmenityItemDistance>
                </AmenityItem>
              )
            })}

            {hasMore && isExpanded && (
              <AmenityShowMore onClick={showMoreItems}>
                {remainingCount <= 10
                  ? t('categoryCard.showAll', {
                      count: remainingCount,
                    })
                  : t('categoryCard.showMore', {
                      count: remainingCount,
                    })}
              </AmenityShowMore>
            )}
          </>
        )}
      </AmenityList>
    </AmenityCategoryCard>
  )
}

export default AmenityCard
